import { useCallback, useMemo } from 'react'

import { useEventType } from 'features/event_type_provider'

import { BaseSection } from './base_section'
import { getEventDetailsPanelKeys } from './components/event_details/utils'
import {
  getEventTypeLocks as getEditFormEventTypeLocks,
  getFormatValuesToServerData as getEditFormFormatValuesToServerData,
  getInitialValues as getEditFormInitialValues,
  getPanelFieldsMapping as getEditFormPanelFieldsMapping,
  getValidationSchemaForPanelKeys as getEditFormValidationSchemaForPanelKeys
} from './edit_form/utils'
import {
  getEventTypeLocks as getMoreOptionsEventTypeLocks,
  getFormatValuesToServerData as getMoreOptionsFormatValuesToServerData,
  getInitialValues as getMoreOptionsInitialValues,
  getMoreOptionsPanelKeys,
  getPanelFieldsMapping as getMoreOptionsPanelFieldsMapping,
  getValidationSchemaForPanelKeys as getMoreOptionsValidationSchemaForPanelKeys,
  valuesMapper as moreOptionsValuesMapper
} from './more_options/utils'
import { Panels } from './panels'
import { valuesMapper as editETFormValuesMapper } from './utils'
import { useResetUpdateMutationsCache, useUpdateEventTypeMutation } from '../../api'
import { useGetDefaultAvailabilityRules } from '../../utils'
import { AccordionProvider } from '../components/accordion'
import { SelectedSectionsProvider } from '../components/selected_sections'

export const eventTypeEditorPanelsFormConfigs = {
  eventDetails: {
    getApplicablePanelKeys: (eventType) => getEventDetailsPanelKeys({ ...eventType, paymentsUpfrontVariant: null }),
    getEventTypeLocks: (eventType) => getEditFormEventTypeLocks(eventType, { paymentsUpfrontVariant: null }),
    getFormatValuesToServerData: (eventType) => getEditFormFormatValuesToServerData({
      eventType,
      paymentsUpfrontVariant: null
    }),
    getInitialValues: (eventType, defaultAvailabilityRules) => getEditFormInitialValues({
      eventType,
      defaultAvailabilityRules,
      paymentsUpfrontVariant: null
    }),
    getPanelFieldsMapping: (eventType) => getEditFormPanelFieldsMapping({
      eventType,
      paymentsUpfrontVariant: null
    }),
    getValidationSchemaForPanelKeys: (eventType, panelKeys) => getEditFormValidationSchemaForPanelKeys({
      eventType,
      paymentsUpfrontVariant: null
    }, panelKeys),
    panelPropsByKey: {
      payment: {
        trackingSection: 'initial_editor_page'
      }
    },
    valuesMapper: editETFormValuesMapper
  },
  moreOptions: {
    getApplicablePanelKeys: (eventType) => getMoreOptionsPanelKeys({ ...eventType, showPaymentsUpfront: false }),
    getEventTypeLocks: (eventType) => getMoreOptionsEventTypeLocks(eventType, { showPaymentsUpfront: false }),
    getFormatValuesToServerData: (eventType) => getMoreOptionsFormatValuesToServerData({
      eventType,
      showPaymentsUpfront: false
    }),
    getInitialValues: (eventType, defaultAvailabilityRules) => getMoreOptionsInitialValues({
      eventType,
      defaultAvailabilityRules,
      showPaymentsUpfront: false
    }),
    getPanelFieldsMapping: (eventType) => getMoreOptionsPanelFieldsMapping({
      eventType,
      showPaymentsUpfront: false
    }),
    getValidationSchemaForPanelKeys: (eventType, panelKeys) => getMoreOptionsValidationSchemaForPanelKeys(
      eventType, { showPaymentsUpfront: false }, panelKeys
    ),
    panelPropsByKey: {},
    valuesMapper: moreOptionsValuesMapper
  }
}

export const eventTypeEditorCrudPanelsFormConfigs = {
  ...eventTypeEditorPanelsFormConfigs,
  eventDetails: {
    ...eventTypeEditorPanelsFormConfigs.eventDetails,
    getValidationSchemaForPanelKeys: (eventType, panelKeys) => getEditFormValidationSchemaForPanelKeys({
      eventType,
      paymentsUpfrontVariant: null,
      requireLocationKind: true,
      useCrudValidationMessages: true,
    }, panelKeys),
  },
  moreOptions: {
    ...eventTypeEditorPanelsFormConfigs.moreOptions,
    getFormatValuesToServerData: (eventType) => getMoreOptionsFormatValuesToServerData({
      eventType,
      showPaymentsUpfront: false,
      excludeLocation: true
    }),
    getValidationSchemaForPanelKeys: (eventType, panelKeys) => getMoreOptionsValidationSchemaForPanelKeys(
      eventType, { showPaymentsUpfront: false, useCrudValidationMessages: true }, panelKeys
    ),
  },
}

export const EventTypeEditorPanelsForm = ({
  formKey,
  panelKeys: requestedPanelKeys,
  panelPropsByKey = {}
}) => {
  const eventType = useEventType()
  const defaultAvailabilityRules = useGetDefaultAvailabilityRules()
  const formConfig = eventTypeEditorPanelsFormConfigs[formKey]
  const [updateEventType, { isLoading, error }] = useUpdateEventTypeMutation({ id: eventType.id })

  useResetUpdateMutationsCache({ id: eventType.id })

  const panelKeys = useMemo(() => {
    const applicablePanelKeys = formConfig.getApplicablePanelKeys(eventType)

    return requestedPanelKeys.filter((panelKey) => applicablePanelKeys.includes(panelKey))
  }, [eventType, formConfig, requestedPanelKeys])

  const etLocks = formConfig.getEventTypeLocks(eventType)
  const lockingDisables = Object.values(etLocks).every(Boolean)

  const validationSchema = useMemo(
    () => formConfig.getValidationSchemaForPanelKeys(eventType, panelKeys),
    [eventType, formConfig, panelKeys]
  )

  const defaultOpenKeys = useMemo(() => panelKeys.slice(0, 1), [panelKeys])

  const handleSubmit = useCallback((attributes) => {
    if (isLoading) return Promise.resolve()

    return updateEventType({ eventType, attributes })
  }, [eventType, isLoading, updateEventType])

  return (
    <SelectedSectionsProvider resetOnChangeOf={eventType.id}>
      <AccordionProvider allowMultiple={false} defaultOpenKeys={defaultOpenKeys}>
        <BaseSection
          formatValues={formConfig.getFormatValuesToServerData(eventType)}
          initialValues={formConfig.getInitialValues(eventType, defaultAvailabilityRules)}
          panelFieldsMapping={formConfig.getPanelFieldsMapping(eventType)}
          validationSchema={validationSchema}
          valuesMapper={formConfig.valuesMapper}
          externalError={error}
          onSubmit={handleSubmit}
        >
          <Panels
            panelKeys={panelKeys}
            etLocks={etLocks}
            lockingDisables={lockingDisables}
            panelPropsByKey={{
              ...formConfig.panelPropsByKey,
              ...panelPropsByKey
            }}
          />
        </BaseSection>
      </AccordionProvider>
    </SelectedSectionsProvider>
  )
}
